import { LabMicrobiomeTestResult } from '.';
import { Study, ResearchCategory, Taxonomy } from '../../generated';
import { MicrobiomeQuestionnaire } from '../questionaire';
import { BacteriaThreshold } from '../bacteria-thershold';
import { TaxonomyDiversity } from '../taxonomy-diversity';
import { BabyDiet } from '../user';
import { MicrobiomeTestResult } from './lab-microbiome-result';
import { ResearchCategorySummary } from './microbiome-test';
import { ReportRecommendation } from './report-recommendation';

export enum BacteriaAttentionAction {
  increase = 'increase',
  decrease = 'decrease',
  maintain = 'maintain',
}

export enum BacteriaAttentionCategory {
  key = 'key',
  disruptive = 'disruptive',
  condition = 'condition',
}

export interface Bacteria {
  name: string;
  taxonomy: Taxonomy;
  percentage: number;
  comparator: string;
  threshold: number;
}

export interface BacteriaResearch {
  research: {
    name: string;
    taxonomy: Taxonomy;
    category: ResearchCategory;
    description: string[];
    condition: string[];
    ansector?: string;
  };
  percentage: number;
  classified: number;
  other: number;
  unclassified: number;
  protective: number;
  disruptive: number;
  neutral: number;
  description?: string[]
}

export interface BacteriaResearchSummary {
  bacteria: BacteriaResearch;
  species: BacteriaResearch[];
}

export interface KeyBacteria extends Bacteria {
  category: BacteriaAttentionCategory.key;
  action: BacteriaAttentionAction.increase | BacteriaAttentionAction.maintain;
  condition?: string;
  categoryType?: string;
  description?: string[];
}

export interface DisruptiveBacteria extends Bacteria {
  category: BacteriaAttentionCategory.disruptive;
  action: BacteriaAttentionAction.increase | BacteriaAttentionAction.decrease;
  condition?: string;
  categoryType?: string;
  description?: string[];
}

export interface ConditionBacteria extends Bacteria {
  category: BacteriaAttentionCategory.condition;
  action: BacteriaAttentionAction;
  condition: string;
  categoryType: string;
  description?: string[];
}

export type BacteriaAttention =
  | KeyBacteria
  | DisruptiveBacteria
  | ConditionBacteria;

export interface ReportSummary {
  diversity: {
    score: number;
    good: boolean;
  };
  balance: {
    protective: number;
    disruptive: number;
    neutral: number;
    score: number;
    good: boolean;
  };
  disruptive: {
    bacteria: DisruptiveBacteria[];
    good: boolean;
  };
  keyBacteria: {
    bacteria: KeyBacteria[];
    good: boolean;
  };
  condition: { [key in string]: ConditionBacteria[] };
}

export interface ReportStats {
  diversity: number;
  balance: number;
  disruptive: number;
  keyBacteria: number;
  attention: number;
  recommendation: number;
  summary: number;
}

export interface BacteriaReport {
  threshold: BacteriaThreshold;
  percentage: number;
  unclassified: number;
  classified: number;
  other: number;
  protective: number;
  disruptive: number;
  neutral: number;
  unknown: number;
  description?: string[]
}

export type StudyReport = ResearchCategorySummary & {
  id: string;
  study: Study;
  ageInMonths: number;
  trimester: number;
  condition: string[];
  diet: BabyDiet[];
  results: LabMicrobiomeTestResult[];
  summary: MicrobiomeTestResult[];
  bacteria: BacteriaReport[];
  questionnaire: MicrobiomeQuestionnaire | null;
  reportedAt?: string | null;
  diversity: TaxonomyDiversity;
  stats: ReportStats;
  recommendations: ReportRecommendation[];
};
